import * as React from 'react';
import { Button } from '@ooh/ui/components/button';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';

/**
 * Double-confirmation dialog for public-link regenerate/revoke — the destructive
 * control stays disabled until the user checks an explicit acknowledgement.
 * Renders nothing when closed so the acknowledgement always resets between
 * openings (same unmount-to-reset pattern as TrashConfirmDialog).
 *
 * public-offer-link / Offer Workspace Public Link Controls
 */
interface PublicLinkConfirmDialogProps {
  isOpen: boolean;
  title: string;
  body: string;
  acknowledgementLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}

export function PublicLinkConfirmDialog({ isOpen, ...rest }: PublicLinkConfirmDialogProps) {
  if (!isOpen) {
    return null;
  }

  return <PublicLinkConfirmDialogContent {...rest} />;
}

function PublicLinkConfirmDialogContent({
  title,
  body,
  acknowledgementLabel,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
}: Omit<PublicLinkConfirmDialogProps, 'isOpen'>) {
  const [acknowledged, setAcknowledged] = React.useState(false);

  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{title}</span>}
      footer={
        <>
          <Button
            data-testid="public-link-confirm-dialog-cancel-btn"
            onClick={onClose}
            size="sm"
            type="button"
            variant="outline"
          >
            {cancelLabel}
          </Button>
          <Button
            data-testid="public-link-confirm-dialog-confirm-btn"
            disabled={!acknowledged}
            onClick={onConfirm}
            size="sm"
            type="button"
            variant="destructive"
          >
            {confirmLabel}
          </Button>
        </>
      }
      onClose={onClose}
      testId="public-link-confirm-dialog"
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{body}</p>
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
          <Checkbox
            checked={acknowledged}
            data-testid="public-link-confirm-dialog-ack-checkbox"
            onCheckedChange={(value) => setAcknowledged(value !== false)}
          />
          {acknowledgementLabel}
        </label>
      </div>
    </ModalDialog>
  );
}
